<script setup lang="ts">
/**
 * The board.
 *
 * A column-free grid that reflows: one ticket wide on a small screen above a
 * pass, four across on a wall display. Nothing is scrolled horizontally, ever —
 * a cook cannot swipe a wall-mounted screen with both hands full.
 *
 * OLDEST FIRST, which the server already sorts by fired time. That order is not
 * cosmetic: it is the order the food has to leave, and a board that sorts by
 * anything else invites a kitchen to work the easy tickets first.
 */
import { computed, ref } from "vue";
import type { Translator } from "@gf/kernel/i18n";
import TicketCard from "./TicketCard.vue";
import SoldOutSheet from "./SoldOutSheet.vue";
import { allDay, type BoardTicket } from "../station";

const props = defineProps<{
  tickets: readonly BoardTicket[];
  stationName: string;
  t: Translator;
  online: boolean;
  queued: number;
  /** Unacknowledged selection ids, by ticket. */
  unacknowledged: Readonly<Record<string, readonly string[]>>;
  /** The languages this build carries. Absent leaves the control off. */
  locales?: readonly string[];
  locale?: string;
  /** The ticket just bumped, while it can still be taken back. */
  recallable?: BoardTicket | null;
  /** Per-dish Start / Hotovo; off, a tap on a ticket sends it. */
  detailed?: boolean;
  /** The training venue: practice orders, not to be cooked. */
  training?: boolean;
  /** The venue does not have the kitchen display module: said in words, not an empty board. */
  disabled?: boolean;
  /** This station's dishes, and which have run out. Absent: no sold-out control. */
  menuItems?: readonly { itemRef: string; name: string; soldOut: boolean }[];
}>();

const emit = defineEmits<{
  acknowledge: [{ ticketId: string; selectionId: string }];
  start: [{ ticketId: string; selectionId: string }];
  finish: [{ ticketId: string; selectionId: string }];
  bump: [ticketId: string];
  recall: [ticketId: string];
  locale: [code: string];
  detailed: [on: boolean];
  toggleSoldOut: [itemRef: string, soldOut: boolean];
}>();

const waiting = computed(() => allDay(props.tickets));
const soldOut = computed(() => (props.menuItems ?? []).filter((i) => i.soldOut).map((i) => i.name));
const sheet = ref(false);
const count = (milli: number): string => String(milli / 1_000);
</script>

<template>
  <div class="board">
    <header class="board__bar">
      <h1 class="board__station">{{ stationName }}</h1>
      <span v-if="training" class="board__training" role="status" data-testid="training">{{ t.t("kds.training") }}</span>
      <!-- A bump made by mistake, taken back in one press while the moment
           lasts. In the bar, not over the grid: it must never cover a ticket. -->
      <div v-if="recallable" class="board__undo" role="status" data-testid="recall">
        <span>{{ t.t("kds.bumped", { table: recallable.tableName ?? recallable.tableRef }) }}</span>
        <button type="button" class="board__undo-btn" @click="emit('recall', recallable.ticketId)">
          {{ t.t("kds.recall") }}
        </button>
      </div>
      <p class="board__status">
        <!-- A kitchen screen that has lost the network keeps working, and says
             so. Bumps queue and drain; the food does not wait for a router. -->
        <span v-if="!online" class="board__offline">{{ t.t("kds.offline") }}</span>
        <span v-if="queued > 0" class="board__queued">{{ t.t("kds.queued", { count: queued }) }}</span>
        <span class="board__count">{{ t.t("kds.openTickets", { count: tickets.length }) }}</span>
        <!--
          THE LANGUAGE, on the bar rather than in a settings screen the board
          does not have. A brigade changes; the person on the pass tonight may
          read Ukrainian far better than Czech, and a wall-mounted screen has
          nowhere else to put this.

          `aria-pressed` rather than a class, so the current language is
          announced and not only coloured.
        -->
        <span v-if="locales && locales.length > 1" class="board__lang" role="group" :aria-label="t.t('kds.language')">
          <button
            v-for="code in locales"
            :key="code"
            type="button"
            class="board__lang-btn"
            :aria-pressed="code === locale"
            @click="$emit('locale', code)"
          >
            {{ code.toUpperCase() }}
          </button>
        </span>
      </p>
    </header>

    <!-- What is waiting, and what has run out: the two numbers a pass works by. -->
    <div class="board__strip">
      <p v-if="waiting.length > 0" class="board__allday" data-testid="all-day">
        <span class="board__label">{{ t.t("kds.allDay") }}</span>
        <span v-for="w in waiting" :key="w.name" class="board__dish">
          <strong>{{ count(w.quantityMilli) }}×</strong> {{ w.name }}
        </span>
      </p>
      <p v-if="soldOut.length > 0" class="board__soldout" data-testid="sold-out">
        <span class="board__label">{{ t.t("kds.soldOut") }}</span>
        {{ soldOut.join(" · ") }}
      </p>
      <span class="board__tools">
        <button
          type="button"
          class="board__tool"
          data-testid="detailed"
          :aria-pressed="detailed ? 'true' : 'false'"
          @click="emit('detailed', !detailed)"
        >
          {{ t.t("kds.detailed") }}
        </button>
        <button v-if="menuItems" type="button" class="board__tool" data-testid="open-sold-out" @click="sheet = true">
          {{ t.t("kds.soldOutOpen") }}
        </button>
      </span>
    </div>

    <SoldOutSheet
      v-if="sheet && menuItems"
      :t="t"
      :items="menuItems"
      @toggle="(itemRef, out) => emit('toggleSoldOut', itemRef, out)"
      @close="sheet = false"
    />

    <div v-if="disabled" class="board__disabled" role="status" data-testid="kds-disabled">
      <p class="board__disabled-title">{{ t.t("kds.disabled.title") }}</p>
      <p>{{ t.t("kds.disabled.lead") }}</p>
    </div>
    <p v-else-if="tickets.length === 0" class="board__empty">{{ t.t("kds.allClear") }}</p>

    <div v-else class="board__grid">
      <TicketCard
        v-for="ticket in tickets"
        :key="ticket.ticketId"
        :ticket="ticket"
        :t="t"
        :unacknowledged="unacknowledged[ticket.ticketId] ?? []"
        :detailed="detailed ?? false"
        @acknowledge="emit('acknowledge', { ticketId: ticket.ticketId, selectionId: $event })"
        @start="emit('start', { ticketId: ticket.ticketId, selectionId: $event })"
        @finish="emit('finish', { ticketId: ticket.ticketId, selectionId: $event })"
        @bump="emit('bump', ticket.ticketId)"
      />
    </div>

  </div>
</template>

<style scoped>
/*
  MyMonday's frame, in the dark (2026-09-25): the board is the canvas, tickets
  are the raised cards on it, and the bar and strip are quiet so the tickets
  are the loudest thing in the room.
*/
.board {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-2);
  min-height: 100vh;
  padding: var(--gf-space-2);
  background: var(--gf-color-canvas);
}

.board__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gf-space-2);
  /* Tall enough for the undo, so the grid does not jump when it comes and goes. */
  min-height: calc(var(--gf-target-min) + var(--gf-space-3));
}

/* Sentence case, weight not capitals. */
.board__station {
  margin: 0;
  font-size: var(--gf-text-xl);
  font-weight: var(--gf-weight-semibold);
  letter-spacing: var(--gf-tracking-tight);
}

.board__status {
  display: flex;
  align-items: center;
  gap: var(--gf-space-2);
  margin: 0;
}

/*
   Deliberately quiet, and deliberately not a bump target.

   A kitchen screen is operated with the back of a wrist and a knuckle. These
   sit apart from the ticket columns, at a size that reads from arm's length
   but does not invite a knuckle mid-service.
*/
/* A segmented control, matching the till's — four of these in a row of
   separate outlined boxes reads as four decisions rather than one. */
.board__lang {
  display: flex;
  gap: var(--gf-space-0);
  margin-left: var(--gf-space-2);
  padding: var(--gf-space-0);
  background: var(--gf-color-surface-sunken);
  border: var(--gf-border-thin) solid var(--gf-color-border-subtle);
  border-radius: var(--gf-radius-md);
}

.board__lang-btn {
  /* Deliberately under the kiosk target (72): a knuckle reaching for a bump
     bar mid-service must not land on a language switch. 48, not the 36 it was:
     still well under the target, and no longer under a fingertip's 44. */
  min-width: var(--gf-space-6);
  min-height: var(--gf-space-6);
  border: 0;
  border-radius: var(--gf-radius-sm);
  background: transparent;
  color: var(--gf-color-text-muted);
  font: inherit;
  font-size: var(--gf-text-xs);
  font-weight: var(--gf-weight-bold);
  cursor: pointer;
  transition:
    background-color var(--gf-motion-fast) var(--gf-ease-out),
    color var(--gf-motion-fast) var(--gf-ease-out);
}

/* The selected segment is raised out of the track — neutral, never the
   accent, which on this screen would read as a warning — and RINGED. On the
   dark board a raised segment and its sunken track differ by a few per cent
   of lightness and a drop shadow has nothing darker to fall on, so without
   the 3:1 ring the chosen language was a matter of guessing. */
.board__lang-btn[aria-pressed="true"] {
  background: var(--gf-color-surface-raised);
  color: var(--gf-color-text);
  box-shadow: inset 0 0 0 var(--gf-border-thin) var(--gf-color-border);
}

.board__lang-btn:focus-visible {
  outline: var(--gf-focus-width) solid var(--gf-color-focus);
  outline-offset: var(--gf-focus-offset);
}

/* A blocking state keeps its solid bar: the board must say it is offline
   louder than anything else in the header. */
.board__offline {
  padding: var(--gf-space-1) var(--gf-space-2);
  border-radius: var(--gf-radius-md);
  background: var(--gf-color-warning);
  color: var(--gf-color-on-warning);
  font-weight: var(--gf-weight-bold);
}

.board__queued,
.board__count {
  color: var(--gf-color-text-muted);
  font-variant-numeric: tabular-nums;
}

.board__undo {
  display: flex;
  align-items: center;
  gap: var(--gf-space-3);
  margin-right: auto;
  padding: var(--gf-space-1) var(--gf-space-1) var(--gf-space-1) var(--gf-space-3);
  border-radius: var(--gf-radius-lg);
  background: var(--gf-color-surface-raised);
  border: var(--gf-border-thin) solid var(--gf-color-border-subtle);
  box-shadow: var(--gf-shadow-md);
  font-size: var(--gf-text-lg);
  font-weight: var(--gf-weight-semibold);
}

.board__undo-btn {
  min-height: var(--gf-target-min);
  padding: 0 var(--gf-space-4);
  border: var(--gf-border-thin) solid var(--gf-color-border);
  border-radius: var(--gf-radius-md);
  background: var(--gf-color-surface-active);
  color: var(--gf-color-text);
  font: inherit;
  font-weight: var(--gf-weight-bold);
  cursor: pointer;
  transition: background-color var(--gf-motion-fast) var(--gf-ease-out);
}

.board__undo-btn:active {
  background: var(--gf-color-surface-hover);
}

.board__undo-btn:focus-visible {
  outline: var(--gf-focus-width) solid var(--gf-color-focus);
  outline-offset: var(--gf-focus-offset);
}

/* Practice: loud enough to read from the stove. */
.board__training {
  padding: var(--gf-space-1) var(--gf-space-3);
  border-radius: var(--gf-radius-md);
  background: var(--gf-color-warning);
  color: var(--gf-color-on-warning);
  font-size: var(--gf-text-lg);
  font-weight: var(--gf-weight-bold);
}

.board__strip {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--gf-space-2) var(--gf-space-4);
  padding: var(--gf-space-2) var(--gf-space-3);
  border: var(--gf-border-thin) solid var(--gf-color-border-subtle);
  border-radius: var(--gf-radius-lg);
  background: var(--gf-color-surface);
}

.board__allday,
.board__soldout {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--gf-space-1) var(--gf-space-3);
  margin: 0;
  font-size: var(--gf-text-lg);
}

.board__soldout {
  color: var(--gf-color-danger);
  font-weight: var(--gf-weight-semibold);
}

/* A quiet label, sentence case: read once, then skipped. */
.board__label {
  color: var(--gf-color-text-muted);
  font-size: var(--gf-text-sm);
  font-weight: var(--gf-weight-semibold);
}

.board__dish strong {
  font-variant-numeric: tabular-nums;
}

.board__tools {
  display: flex;
  gap: var(--gf-space-2);
  margin-left: auto;
}

/* Like the language switch: reachable, and not where a knuckle lands. */
.board__tool {
  min-height: var(--gf-space-6);
  padding: 0 var(--gf-space-3);
  border: var(--gf-border-thin) solid var(--gf-color-border-subtle);
  border-radius: var(--gf-radius-md);
  background: var(--gf-color-surface-raised);
  color: var(--gf-color-text);
  font: inherit;
  font-size: var(--gf-text-sm);
  font-weight: var(--gf-weight-semibold);
  box-shadow: var(--gf-shadow-sm);
  cursor: pointer;
  transition: background-color var(--gf-motion-fast) var(--gf-ease-out);
}

.board__tool:active {
  background: var(--gf-color-surface-hover);
}

/* ON is unmistakable in the dark: pressed in, with a firm light ring. */
.board__tool[aria-pressed="true"] {
  color: var(--gf-color-text);
  background: var(--gf-color-surface-active);
  border-color: transparent;
  box-shadow: inset 0 0 0 var(--gf-border-thick) var(--gf-color-border-strong);
}

.board__tool:focus-visible {
  outline: var(--gf-focus-width) solid var(--gf-color-focus);
  outline-offset: var(--gf-focus-offset);
}

.board__disabled {
  max-width: 36rem;
  margin: var(--gf-space-8) auto;
  padding: var(--gf-space-6);
  border: var(--gf-border-thin) solid var(--gf-color-border-subtle);
  border-radius: var(--gf-radius-lg);
  background: var(--gf-color-surface);
  color: var(--gf-color-text-muted);
  text-align: center;
}

.board__disabled-title {
  margin: 0 0 var(--gf-space-2);
  color: var(--gf-color-text);
  font-size: var(--gf-text-xl);
  font-weight: var(--gf-weight-semibold);
}

.board__empty {
  margin: auto;
  color: var(--gf-color-text-muted);
  font-size: var(--gf-text-lg);
}

/*
 * Reflows rather than scrolls sideways. A cook cannot swipe a wall-mounted
 * screen with both hands full.
 */
.board__grid {
  display: grid;
  /* 18rem: four columns of ~300 px on a 1280 screen, three on a 1024. */
  grid-template-columns: repeat(auto-fill, minmax(18rem, 1fr));
  gap: var(--gf-space-2);
  align-items: start;
}

@media (prefers-reduced-motion: reduce) {
  .board__lang-btn,
  .board__undo-btn,
  .board__tool {
    transition: none;
  }
}
</style>
